import {
  forwardRef,
  useEffect,
  useRef,
  useReducer,
  useCallback,
  useMemo,
} from "react";
import merge from "deepmerge";
import TextField from "./TextField";
import HiddenField from "./HiddenField";
import styles from "@/styles/Form.module.scss";

const initializeValue = {
  passcode: "",
  passcodes: { 1: null, 2: null, 3: null, 4: null },
};

const reducer = (state, action) => {
  switch (action.type) {
    case "change":
      const newState = merge(state, { passcodes: action.value });
      newState.passcode = Object.values(newState.passcodes).join("");
      return newState;
    case "reset":
      return initializeValue;
  }
};

const PasscodeField = forwardRef((props, ref) => {
  const { error, initialFocus, defaultValue, onChange, onInputCompleted } =
    props;
  const [state, dispatch] = useReducer(reducer, initializeValue);

  const passcodeRefs = {
    1: useRef(),
    2: useRef(),
    3: useRef(),
    4: useRef(),
  };

  const [val1, val2, val3, val4] = useMemo(() => {
    if (defaultValue && defaultValue.length === 4) {
      return defaultValue.split("");
    } else {
      return [];
    }
  }, [defaultValue]);

  const handleChange = useCallback((idx) => {
    return (e) => {
      dispatch({ type: "change", value: { [idx]: e.target.value } });
      if (e.target.value) {
        const next = passcodeRefs[idx + 1];
        if (next && next.current) next.current.focus();
      } else {
        const prev = passcodeRefs[idx - 1];
        if (prev && prev.current) prev.current.focus();
      }
    };
  }, []);

  const handleFocus = useCallback((e) => {
    e.target.select();
  }, []);

  const handleInput = useCallback((e) => {
    if (!/^\d{1}$/.test(e.target.value)) {
      e.target.value = null;
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  }, []);

  useEffect(() => {
    if (state.passcode) {
      if (onChange) onChange(state.passcode);
      if (onInputCompleted && state.passcode.length === 4)
        onInputCompleted(state.passcode);
    }
  }, [state.passcode]);

  useEffect(() => {
    let timeoutId;
    if (passcodeRefs[1] && passcodeRefs[1].current) {
      timeoutId = setTimeout(() => {
        if (initialFocus) passcodeRefs[1].current.focus();
      }, 10);
    }
    return () => clearTimeout(timeoutId);
  }, [initialFocus]);

  return (
    <div className={styles["passcodeFields"]}>
      <div className={styles.passcodeFieldsInner}>
        <TextField
          ref={passcodeRefs[1]}
          name="passcode1"
          maxLength="1"
          inputMode="numeric"
          onInput={handleInput}
          onFocus={handleFocus}
          onChange={handleChange(1)}
          className={styles.passcodeField}
          defaultValue={val1 || ""}
          autoComplete="off"
          textSecurity
        />
        <TextField
          ref={passcodeRefs[2]}
          name="passcode2"
          maxLength="1"
          inputMode="numeric"
          onInput={handleInput}
          onFocus={handleFocus}
          onChange={handleChange(2)}
          className={styles.passcodeField}
          defaultValue={val2 || ""}
          autoComplete="off"
          textSecurity
        />
        <TextField
          ref={passcodeRefs[3]}
          name="passcode3"
          maxLength="1"
          inputMode="numeric"
          onInput={handleInput}
          onFocus={handleFocus}
          onChange={handleChange(3)}
          className={styles.passcodeField}
          defaultValue={val3 || ""}
          autoComplete="off"
          textSecurity
        />
        <TextField
          ref={passcodeRefs[4]}
          name="passcode4"
          maxLength="1"
          inputMode="numeric"
          onInput={handleInput}
          onFocus={handleFocus}
          onChange={handleChange(4)}
          className={styles.passcodeField}
          defaultValue={val4 || ""}
          autoComplete="off"
          textSecurity
        />
      </div>

      <HiddenField
        ref={ref}
        name="passcode"
        value={state.passcode}
        error={error}
      />
    </div>
  );
});

PasscodeField.displayName = "PasscodeField";
export default PasscodeField;
